import { Link } from "react-router-dom";
import { ArrowRight, Brush, Home as HomeIcon, Building2, Sparkles, ShieldCheck, Clock4, Hammer, Heart } from "lucide-react";
import { SITE, SERVICES } from "@/lib/site";
import useSEO from "@/lib/useSEO";
import EkgLine from "@/components/EkgLine";

const SERVICE_ICONS = {
    interior: HomeIcon,
    exterior: Brush,
    commercial: Building2,
    specialty: Sparkles,
};

export default function Home() {
    useSEO({
        title: `${SITE.brand} — Professional Painting in ${SITE.serviceArea}`,
        description: `Interior, exterior, commercial, and specialty painting in ${SITE.serviceArea}. Licensed, insured, and built around quality craftsmanship. Get a free estimate today.`,
    });

    return (
        <main data-testid="page-home">
            {/* ============================== HERO ============================== */}
            <section
                className="hero-grain"
                style={{
                    position: "relative",
                    background: "var(--brand-cream)",
                    overflow: "hidden",
                }}
            >
                <div
                    className="brand-container section-pad"
                    style={{
                        display: "grid",
                        gap: "3rem",
                        gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 0.95fr)",
                        alignItems: "center",
                    }}
                >
                    <div className="fade-rise">
                        <span className="eyebrow" data-testid="hero-eyebrow">
                            Commercial Â· Residential Â· Utah & Surrounding States
                        </span>
                        <h1
                            className="h1"
                            style={{ marginTop: "1rem", marginBottom: "1.25rem" }}
                            data-testid="hero-headline"
                        >
                            Professional painting that{" "}
                            <span style={{ color: "var(--brand-red)" }}>
                                brings your space back to life
                            </span>
                            .
                        </h1>
                        <p
                            style={{
                                fontSize: "1.125rem",
                                color: "var(--brand-muted)",
                                maxWidth: 560,
                                marginBottom: "2rem",
                            }}
                            data-testid="hero-subhead"
                        >
                            Commercial Â· Specialty Â· Exterior Â· Interior â proudly
                            serving Utah and surrounding states, as well as our
                            friends and neighbors here at home.
                        </p>

                        <div
                            style={{
                                display: "flex",
                                gap: "0.75rem",
                                flexWrap: "wrap",
                            }}
                        >
                            <Link
                                to="/contact"
                                className="btn btn-primary"
                                data-testid="hero-cta-primary"
                            >
                                Get a Free Estimate
                                <ArrowRight size={18} />
                            </Link>
                            <Link
                                to="/services"
                                className="btn btn-secondary"
                                data-testid="hero-cta-secondary"
                            >
                                See Our Services
                            </Link>
                        </div>

                        <ul
                            style={{
                                marginTop: "2.5rem",
                                listStyle: "none",
                                padding: 0,
                                display: "flex",
                                gap: "1.75rem",
                                flexWrap: "wrap",
                                color: "var(--brand-dark)",
                                fontSize: "0.875rem",
                                fontWeight: 600,
                            }}
                            data-testid="hero-trust-row"
                        >
                            <li
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                }}
                            >
                                <ShieldCheck
                                    size={18}
                                    color="var(--brand-red)"
                                />
                                Licensed & insured
                            </li>
                            <li
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                }}
                            >
                                <Clock4 size={18} color="var(--brand-red)" />
                                Start dates and finish dates we actually hit.
                            </li>
                            <li
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                }}
                            >
                                <Heart size={18} color="var(--brand-red)" />
                                Locally owned
                            </li>
                        </ul>
                    </div>

                    <div
                        className="fade-rise delay-2"
                        style={{ position: "relative" }}
                    >
                        <div
                            style={{
                                position: "absolute",
                                inset: "-1.5rem -1.5rem auto auto",
                                width: "70%",
                                height: "70%",
                                background:
                                    "linear-gradient(135deg, rgba(20,27,202,0.10), rgba(20,27,202,0.05))",
                                borderRadius: 16,
                                zIndex: 0,
                            }}
                            aria-hidden="true"
                        />
                        <div
                            style={{
                                position: "relative",
                                zIndex: 1,
                                borderRadius: 16,
                                overflow: "hidden",
                                boxShadow:
                                    "0 24px 60px rgba(26,26,26,0.18)",
                                border: "8px solid #fff",
                                aspectRatio: "4 / 3",
                                background: "var(--brand-surface)",
                            }}
                            data-testid="hero-image-frame"
                        >
                            <img
                                src="/img/service-commercial.png"
                                alt="A commercial painting crew at work"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                }}
                                onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                }}
                            />
                        </div>
                        <div
                            style={{
                                position: "absolute",
                                left: "-1rem",
                                bottom: "-1.25rem",
                                background: "#fff",
                                borderRadius: 12,
                                padding: "0.9rem 1.1rem",
                                boxShadow:
                                    "0 12px 30px rgba(26,26,26,0.12)",
                                display: "flex",
                                gap: 12,
                                alignItems: "center",
                                zIndex: 2,
                            }}
                            data-testid="hero-floating-badge"
                        >
                            <div
                                style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 8,
                                    background: "var(--brand-red)",
                                    color: "#fff",
                                    display: "grid",
                                    placeItems: "center",
                                }}
                            >
                                <Hammer size={20} />
                            </div>
                            <div>
                                <div
                                    style={{
                                        fontFamily: "var(--font-display)",
                                        fontWeight: 800,
                                        fontSize: "1rem",
                                    }}
                                >
                                    Free, no-obligation quotes
                                </div>
                                <div
                                    style={{
                                        color: "var(--brand-muted)",
                                        fontSize: "0.8125rem",
                                    }}
                                >
                                    Walk-throughs scheduled within 48 hours
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============================== SERVICES ============================== */}
            <section
                className="section-pad"
                style={{ background: "#fff" }}
                data-testid="services-overview-section"
            >
                <div className="brand-container">
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "space-between",
                            gap: "1.5rem",
                            flexWrap: "wrap",
                            marginBottom: "2.5rem",
                        }}
                    >
                        <div>
                            <span className="eyebrow">What We Do</span>
                            <h2 className="h2" style={{ marginTop: "0.85rem" }}>
                                Four kinds of work. One standard.
                            </h2>
                        </div>
                        <p
                            style={{
                                color: "var(--brand-muted)",
                                maxWidth: 440,
                            }}
                        >
                            Whether it's a single accent wall or a 20,000 sq ft
                            commercial repaint, every job gets the same prep,
                            the same crew, and the same finish.
                        </p>
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gap: "1.25rem",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(240px, 1fr))",
                        }}
                    >
                        {SERVICES.map((s) => {
                            const Icon = SERVICE_ICONS[s.slug] || Brush;
                            return (
                                <Link
                                    to="/services"
                                    key={s.slug}
                                    className="brand-card"
                                    data-testid={`service-card-${s.slug}`}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "1rem",
                                        height: "100%",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 10,
                                            background:
                                                "rgba(210, 5, 28, 0.08)",
                                            display: "grid",
                                            placeItems: "center",
                                            color: "var(--brand-red)",
                                        }}
                                    >
                                        <Icon size={22} />
                                    </div>
                                    <div>
                                        <h3 className="h3">{s.title}</h3>
                                        <p
                                            style={{
                                                marginTop: "0.5rem",
                                                color: "var(--brand-muted)",
                                                fontSize: "0.95rem",
                                            }}
                                        >
                                            {s.short}
                                        </p>
                                    </div>
                                    <div
                                        style={{
                                            marginTop: "auto",
                                            color: "var(--brand-red)",
                                            fontWeight: 700,
                                            fontSize: "0.875rem",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                        }}
                                    >
                                        Learn more <ArrowRight size={16} />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ============================== WHY US ============================== */}
            <section
                className="section-pad"
                style={{ background: "var(--brand-cream)" }}
                data-testid="why-us-section"
            >
                <div className="brand-container">
                    <div
                        style={{
                            display: "grid",
                            gap: "3rem",
                            gridTemplateColumns:
                                "minmax(0, 0.9fr) minmax(0, 1.1fr)",
                            alignItems: "center",
                        }}
                    >
                        <div>
                            <span className="eyebrow">Why CPR Paint</span>
                            <h2
                                className="h2"
                                style={{
                                    marginTop: "0.85rem",
                                    marginBottom: "1rem",
                                }}
                            >
                                The work is the hero. The crew is the reason.
                            </h2>
                            <p
                                style={{
                                    color: "var(--brand-muted)",
                                    fontSize: "1.0625rem",
                                    marginBottom: "1.5rem",
                                }}
                            >
                                "CPR" isn't just initials — it's how we think
                                about every project: prep what's broken, restore
                                what's tired, and bring the space back to life.
                                That mindset shows up in clean lines, protected
                                floors, and a jobsite you don't dread coming
                                home to.
                            </p>
                            <Link
                                to="/about"
                                className="btn btn-secondary"
                                data-testid="why-us-cta"
                            >
                                Learn about the company
                                <ArrowRight size={18} />
                            </Link>
                        </div>

                        <ul
                            style={{
                                listStyle: "none",
                                padding: 0,
                                margin: 0,
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(220px, 1fr))",
                                gap: "1rem",
                            }}
                        >
                            {[
                                {
                                    icon: ShieldCheck,
                                    title: "Licensed & insured",
                                    body: "Fully licensed contractor. Coverage you can verify.",
                                },
                                {
                                    icon: Hammer,
                                    title: "Prep is the work",
                                    body: "Patching, sanding, priming — the part that makes paint last.",
                                },
                                {
                                    icon: Clock4,
                                    title: "On the schedule",
                                    body: "Start dates and finish dates we actually hit.",
                                },
                                {
                                    icon: Heart,
                                    title: "Clean jobsites",
                                    body: "Floors covered, dust contained, swept before we leave.",
                                },
                            ].map(({ icon: Icon, title, body }) => (
                                <li
                                    key={title}
                                    className="brand-card"
                                    style={{
                                        padding: "1.25rem",
                                        background: "#fff",
                                    }}
                                    data-testid={`why-us-card-${title
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")
                                        .replace(/&/g, "and")}`}
                                >
                                    <div
                                        style={{
                                            width: 38,
                                            height: 38,
                                            borderRadius: 8,
                                            background:
                                                "rgba(210, 5, 28, 0.08)",
                                            color: "var(--brand-red)",
                                            display: "grid",
                                            placeItems: "center",
                                            marginBottom: "0.85rem",
                                        }}
                                    >
                                        <Icon size={18} />
                                    </div>
                                    <h4
                                        style={{
                                            fontFamily: "var(--font-display)",
                                            fontWeight: 700,
                                            fontSize: "1.0625rem",
                                        }}
                                    >
                                        {title}
                                    </h4>
                                    <p
                                        style={{
                                            color: "var(--brand-muted)",
                                            fontSize: "0.9rem",
                                            marginTop: "0.4rem",
                                        }}
                                    >
                                        {body}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* ============================== SERVICE AREA ============================== */}
            <section
                className="section-pad-sm"
                style={{ background: "#fff", borderTop: "1px solid var(--brand-surface)" }}
                data-testid="service-area-section"
            >
                <div
                    className="brand-container"
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "2rem",
                        flexWrap: "wrap",
                        alignItems: "center",
                    }}
                >
                    <div>
                        <span className="eyebrow">Where We Work</span>
                        <h3
                            className="h3"
                            style={{ marginTop: "0.75rem", fontSize: "1.5rem" }}
                        >
                            Proudly serving Utah and surrounding states.
                        </h3>
                        <p
                            style={{
                                color: "var(--brand-muted)",
                                marginTop: "0.5rem",
                                maxWidth: 520,
                            }}
                        >
                            Based in {SITE.serviceArea} â serving builders,
                            business owners, and homeowners across Utah and
                            the surrounding states, as well as our friends and
                            neighbors here at home.
                        </p>
                    </div>
                    <Link
                        to="/contact"
                        className="btn btn-primary"
                        data-testid="service-area-cta"
                    >
                        Check my address
                        <ArrowRight size={18} />
                    </Link>
                </div>
            </section>

            {/* ============================== FINAL CTA ============================== */}
            <section
                style={{
                    background: "var(--brand-dark)",
                    color: "#fff",
                    position: "relative",
                    overflow: "hidden",
                }}
                data-testid="final-cta-section"
            >
                <EkgLine height={36} />
                <div
                    className="brand-container section-pad"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: "1.25rem",
                    }}
                >
                    <span
                        className="eyebrow"
                        style={{ color: "#fff" }}
                    >
                        Ready when you are
                    </span>
                    <h2
                        className="h2"
                        style={{
                            color: "#fff",
                            maxWidth: 720,
                            fontSize: "clamp(2rem, 4vw, 2.75rem)",
                        }}
                    >
                        Ready to transform your space?
                    </h2>
                    <p
                        style={{
                            color: "#cfcdc6",
                            maxWidth: 560,
                            fontSize: "1.0625rem",
                        }}
                    >
                        Get your free, no-obligation estimate. We'll walk the
                        space, talk through the work, and put a real number on
                        paper.
                    </p>
                    <Link
                        to="/contact"
                        className="btn btn-on-dark"
                        data-testid="final-cta-button"
                        style={{ marginTop: "0.5rem" }}
                    >
                        Get a Free Estimate
                        <ArrowRight size={18} />
                    </Link>
                </div>
            </section>
        </main>
    );
}
